import { resolveSkillResearchScope } from "../../../../shared/skills/runtime/skillResearchScope";
import type { SkillRunContext } from "../../../../shared/skills/runtime/skillTurnBootstrap";
import type { FinsterQueryDataSource } from "../../../api/finster/constants";
import type { AgentToolContext, ToolExecutionPolicy } from "../types";

export type SkillRuntimeScopeResult =
  | {
      readonly status: "applied";
      /** Targets the shared policy withheld; zero on every ordinary run. */
      readonly omittedTargetCount: number;
    }
  | { readonly status: "blocked"; readonly message: string };

/**
 * Host adapter for PowerPoint. The resolver contract, the immutable
 * `SkillRunContext`, and the scope normalization are shared; only the write
 * into PowerPoint's mutable per-turn runtime objects lives here.
 *
 * A `blocked` result leaves both runtime objects untouched, so a caller that
 * ignores it runs without the Skill rather than with a partial one.
 */
export function applySkillRunContextToRuntime(
  context: SkillRunContext,
  toolPolicy: ToolExecutionPolicy,
  agentToolContext: AgentToolContext
): SkillRuntimeScopeResult {
  const outcome = resolveSkillResearchScope(context, {
    dataSources: toolPolicy.finsterDataSources,
    userFileIds: toolPolicy.userFileIds,
  });
  if (outcome.status === "blocked") {
    return { status: "blocked", message: outcome.message };
  }
  const { scope } = outcome;

  // A resolved Skill always owns the source scope, so the query boundary must
  // never substitute the host's default catalog for an omitted array.
  toolPolicy.finsterDataSourceScopeIsAuthoritative = true;
  if (scope.dataSources === undefined) {
    // `null` is the resolver's unrestricted state. Dropping the submitted value
    // omits `data_sources` entirely, which permits every entitled source.
    // Retaining it would let a host default of `[]` mean "no sources".
    delete toolPolicy.finsterDataSources;
  } else {
    toolPolicy.finsterDataSources = scope.dataSources as readonly FinsterQueryDataSource[];
  }
  if (scope.userFileIds !== undefined) {
    toolPolicy.userFileIds = scope.userFileIds;
  }
  agentToolContext.selectedSkillsGuidance = context.guidance;
  return {
    status: "applied",
    omittedTargetCount: outcome.status === "targets-omitted" ? outcome.omittedTargetCount : 0,
  };
}
